/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';
import { useDataFetchIndicator } from '..';

type HookProps = {
  api: vp.Api;
};

type ReturnTuple = [
  AccountInvoicesResponse | undefined,
  (params: AccountInvoicesRequestParams) => void,
  () => void,
  (params: AccountInvoicesRequestParams) => Promise<vp.AccountInvoicesResponse>,
];

export type AccountInvoicesRequestParams = {
  fromDate?: string;
  toDate?: string;
  currency?: vp.SupportedCurrencies | '';
  SearchText?: string;
};

export type AccountInvoicesResponse = {
  success: boolean;
  errorMessage: string;
  accountInvoices: vp.PartnerInvoice[];
  PaginationMetaData?: vp.PaginationMetaData;
};

function useFetchAccountInvoices({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<AccountInvoicesRequestParams>();
  const [response, setResponse] = useState<AccountInvoicesResponse>();
  const [shouldSendRequest, setSendRequest] = useState(false);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  useEffect(() => {
    const sendRequest = async (requestParams: AccountInvoicesRequestParams) => {
      setDataFetchingInfo(true, 'Loading Data', 'fullScreen');

      const { Success, ErrorMessage, Invoices } = await api.fetchAccountInvoices(buildParams(requestParams), source);

      const res = {
        success: Success,
        errorMessage: ErrorMessage,
        accountInvoices: Object.values(Invoices || {}),
      };

      setResponse(res);
      setSendRequest(false);
      setDataFetchingInfo(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams?: AccountInvoicesRequestParams) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse(undefined);
      setSendRequest(false);
    },
    async (requestParams: AccountInvoicesRequestParams) => {
      try {
        setDataFetchingInfo(true, 'loading some data', 'fullScreen');
        const res = await api.fetchAccountInvoices(requestParams, source);
        setResponse(undefined);
        return res;
      } finally {
        setDataFetchingInfo(false);
      }
    },
  ];

  function buildParams(requestParams: AccountInvoicesRequestParams): vp.InvoicesRequest {
    return {
      FromDate: requestParams.fromDate,
      ToDate: requestParams.toDate,
      Currency: requestParams.currency,
      SearchText: requestParams.SearchText,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(useFetchAccountInvoices);
